import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DemoPhoneMockup from "./DemoPhoneMockup";

const DemoSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="container mx-auto px-5 md:px-4">
        {/* Header */}
        <motion.div
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-100 mb-6 border border-emerald-200">
            <Smartphone className="w-3 h-3 text-emerald-600 mr-2" />
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Aperçu de l'Application</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4 leading-tight">
            Découvrez l'expérience <span className="text-emerald-600">CynthIA</span>
          </h2>

          <p className="text-lg md:text-xl text-slate-600">
            Ceci n'est qu'une <strong>partie</strong> de l'expérience. Au-delà de ce tableau de bord, accédez à une
            suite complète d'outils pour comprendre vos plantes.
            <span className="block mt-2 font-medium text-emerald-700">Fini le doute !</span>
          </p>
        </motion.div>

        {/* Phone Mockup Container */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 max-w-6xl mx-auto">
          {/* Phone Mockup */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <DemoPhoneMockup />
          </motion.div>

          {/* Feature highlights */}
          <motion.div
            className="max-w-md space-y-6"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="space-y-4">
              <FeaturePoint
                number="1"
                title="Score de Santé en temps réel"
                description="Voyez d'un coup d'œil si vos plantes vont bien grâce à notre algorithme propriétaire."
              />
              <FeaturePoint
                number="2"
                title="Alertes intelligentes"
                description="Recevez des notifications quand vos plantes ont besoin d'attention, pas avant."
              />
              <FeaturePoint
                number="3"
                title="Prédictions IA"
                description="Arrosez avec précision. Ne le faites plus «parce que c'est mardi», mais parce que c'est nécessaire. Économisez jusqu'à 60% d'eau."
              />
            </div>

            <Button
              size="lg"
              onClick={() => {
                navigate("/shop");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="w-full sm:w-auto mt-8 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full px-8 py-6 text-lg shadow-lg shadow-emerald-500/25 transition-all hover:scale-105"
            >
              Adopter l'assistant CynthIA
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FeaturePoint = ({ number, title, description }: { number: string; title: string; description: string }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div>
      <h3 className="font-bold text-slate-900 mb-1">{title}</h3>
      <p className="text-slate-600 text-sm">{description}</p>
    </div>
  </div>
);

export default DemoSection;
